import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Eye, ChevronDown, ChevronRight, RefreshCw, Lock } from 'lucide-react';
import { clientsAPI, statusAPI, syncAPI } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [customFieldsOpen, setCustomFieldsOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'New',
    notes: '',
  });

  useEffect(() => {
    fetchClients();
    fetchStatuses();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = await statusAPI.getAll();
      setStatuses(response.data.statuses);
    } catch (error) {
      console.error('Failed to load statuses');
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const response = await syncAPI.syncGoogleSheets();
      const { clients_added = 0, clients_updated = 0, clients_removed = 0, message } = response.data || {};
      if (response.data?.clients_added !== undefined) {
        toast.success(
          `Sync terminée : ${clients_added} ajouté(s), ${clients_updated} mis à jour` +
            (clients_removed ? `, ${clients_removed} supprimé(s)` : '')
        );
      } else {
        toast.message(message || 'Sync terminée');
      }
      await fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Erreur de synchronisation');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, formData);
        toast.success('Client updated successfully');
      } else {
        await clientsAPI.create(formData);
        toast.success('Client created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error('Failed to save client');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await clientsAPI.delete(id);
        toast.success('Client deleted successfully');
        fetchClients();
      } catch (error) {
        toast.error('Failed to delete client');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      status: 'New',
      notes: '',
    });
    setEditingClient(null);
    setCustomFieldsOpen(false);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      phone: client.phone,
      status: client.status,
      notes: client.notes || '',
    });
    setCustomFieldsOpen(false);
    setIsDialogOpen(true);
  };

  const filteredClients = clients.filter((client) => {
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    if (
      client.name?.toLowerCase().includes(q) ||
      client.email?.toLowerCase().includes(q) ||
      (client.phone || '').includes(searchTerm)
    ) return true;
    // Also match on stringified custom_fields for sheet-imported columns
    const cf = client.custom_fields || {};
    return Object.values(cf).some((v) => String(v || '').toLowerCase().includes(q));
  });

  const getStatusColor = (status) => {
    const statusObj = statuses.find((s) => s.value === status);
    return statusObj?.color || '#6B7280';
  };

  const customFieldEntries = editingClient?.custom_fields
    ? Object.entries(editingClient.custom_fields)
    : [];
  const hasCustomFields = customFieldEntries.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 sm:space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold font-heading tracking-tight text-foreground">
            Clients
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed text-sm sm:text-base">
            Manage your insurance clients
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            onClick={handleSync}
            disabled={isSyncing}
            variant="outline"
            className="rounded-full px-4 sm:px-6 py-2 font-medium text-sm sm:text-base"
            data-testid="sync-google-sheets-button"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Synchronisation…' : 'Synchroniser maintenant'}
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-4 sm:px-6 py-2 font-medium text-sm sm:text-base"
            data-testid="add-client-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Client
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Search by name, email, phone, or sheet column…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-background border-border focus:border-primary focus:ring-1 focus:ring-primary/20 text-sm sm:text-base"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Mobile: Card view */}
      <div className="lg:hidden space-y-3">{filteredClients.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground bg-card rounded-lg border">
            No clients found. Add your first client to get started.
          </div>
        ) : (
          filteredClients.map((client) => (
            <Card key={client.id} className="border border-border/60">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">{client.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{client.email}</p>
                    <p className="text-sm text-muted-foreground">{client.phone}</p>
                  </div>
                  <span
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${getStatusColor(client.status)}20`,
                      color: getStatusColor(client.status),
                    }}
                  >
                    {client.status}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/clients/${client.id}`)}
                    className="flex-1"
                  >
                    <Eye size={14} className="mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(client)}
                    className="flex-1"
                  >
                    <Edit size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(client.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop: Table view */}
      <div className="hidden lg:block bg-card rounded-lg border border-border/60 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted">
              <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wide">Name</TableHead>
              <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wide">Email</TableHead>
              <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wide">Phone</TableHead>
              <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wide">Status</TableHead>
              <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wide text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No clients found. Add your first client to get started.
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => (
                <TableRow key={client.id} className="hover:bg-muted/50 transition-colors">
                  <TableCell className="font-medium text-foreground">{client.name}</TableCell>
                  <TableCell className="text-muted-foreground">{client.email}</TableCell>
                  <TableCell className="text-muted-foreground">{client.phone}</TableCell>
                  <TableCell>
                    <span
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${getStatusColor(client.status)}20`,
                        color: getStatusColor(client.status),
                      }}
                    >
                      {client.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/clients/${client.id}`)}
                        data-testid={`view-client-${client.id}`}
                      >
                        <Eye size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(client)}
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(client.id)}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </DialogTitle>
            <DialogDescription>
              {editingClient ? 'Update client information' : 'Fill in the details to add a new client'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="client-email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                data-testid="client-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="client-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                data-testid="client-notes-input"
              />
            </div>

            {/* Custom fields — read-only, sourced from Google Sheets */}
            {editingClient && hasCustomFields && (
              <div className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCustomFieldsOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-muted/40 hover:bg-muted/60 text-sm font-medium"
                  data-testid="toggle-custom-fields"
                >
                  <span className="flex items-center gap-2">
                    {customFieldsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    Champs personnalisés ({customFieldEntries.length})
                  </span>
                  <Lock size={12} className="text-muted-foreground" />
                </button>
                {customFieldsOpen && (
                  <div className="p-3 space-y-2 bg-card" data-testid="custom-fields-panel">
                    <p className="text-xs text-muted-foreground italic mb-2">
                      Ces champs sont synchronisés depuis Google Sheets et ne peuvent pas être modifiés ici.
                    </p>
                    <div className="space-y-1">
                      {customFieldEntries.map(([key, value]) => {
                        const isSystem = key.startsWith('_');
                        return (
                          <div
                            key={key}
                            className={`grid grid-cols-[1fr_2fr] gap-2 text-xs px-2 py-1 rounded ${
                              isSystem ? 'bg-muted/60 text-muted-foreground' : 'bg-background'
                            }`}
                            data-testid={`custom-field-${key}`}
                          >
                            <span className="font-mono font-medium truncate" title={key}>
                              {key}
                            </span>
                            <span className="font-mono truncate" title={String(value)}>
                              {value === '' || value === null || value === undefined ? (
                                <em className="text-muted-foreground">(vide)</em>
                              ) : (
                                String(value)
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-primary text-white hover:bg-primary/90"
                data-testid="save-client-button"
              >
                {editingClient ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default Clients;
